import { animate, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, HostBinding, Input, Output } from '@angular/core';
import { take } from 'rxjs/operators';
import { CentruiIconComponent } from '../../../../centrui/src/app/components/centrui-icon/centrui-icon.component';
import { ApiService } from '../../../../centrui/src/app/services/api.service';
import { ModalService } from '../../../../centrui/src/app/services/modal.service';
import { Annotation2Component } from '../../modals/annotation2/annotation2.component';
import { Attachment } from '../../../models/Attachment';
import { LumiService } from '../../../services/lumi.service';
import { ChatMessagePanelRef, ChatMessageSource, LumiChatMessage } from './lumi-chat-message.model';
import { PxTooltipDirective } from '../../../../centrui/src/app/directives/px-tooltip/px-tooltip.directive';

const SOURCE_ROW_STAGGER_MS = 190;
const SOURCE_FADE_IN_UP_DURATION_MS = 200;

@Component({
  selector: 'app-lumi-chat-message',
  imports: [PxTooltipDirective, CommonModule, CentruiIconComponent],
  templateUrl: './lumi-chat-message.component.html',
  styleUrl: './lumi-chat-message.component.scss',
  animations: [
    trigger('fadeInUp', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(0.5rem)' }),
        animate('200ms {{ delay }}ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
    ]),
    trigger('fadeInDelayed', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('200ms {{ delay }}ms ease-out', style({ opacity: 1 })),
      ]),
    ]),
  ],
})
export class LumiChatMessageComponent {
  @HostBinding('class.lumi-chat-message-host--user')
  get hostUserClass(): boolean {
    return this.message?.type === 'user';
  }

  @HostBinding('class.lumi-chat-message-host--bot')
  get hostBotClass(): boolean {
    return this.message?.type === 'bot';
  }

  @Input({ required: true }) message!: LumiChatMessage;
  @Input() sourceNavigationProjectId?: number;
  @Input() sourceNavigationProjectSlug?: string;
  @Input() showThankYou = false;
  @Input() thankYouFadingOut = false;
  @Output() upvote = new EventEmitter<void>();
  @Output() downvote = new EventEmitter<void>();
  @Output() dismissThankYou = new EventEmitter<void>();

  feedbackGiven: 'up' | 'down' | null = null;

  constructor(
    private lumiService: LumiService,
    private apiService: ApiService,
    private modalService: ModalService,
  ) {}

  sourceCitationRowDelay(index: number): number {
    return SOURCE_ROW_STAGGER_MS * (index + 1);
  }

  feedbackHintFadeDelayMs(): number {
    const sourceCount = this.message.sources?.length ?? 0;
    const panelRefCount = this.totalPanelRefCount();
    const n = sourceCount + panelRefCount;
    if (n === 0) return 0;
    return SOURCE_ROW_STAGGER_MS * n + SOURCE_FADE_IN_UP_DURATION_MS;
  }

  totalPanelRefCount(): number {
    return (this.message.panelRefSections ?? []).reduce((sum, section) => sum + section.refs.length, 0);
  }

  panelRefRowDelay(sectionIndex: number, refIndex: number): number {
    const sourceCount = this.message.sources?.length ?? 0;
    let priorPanelRefs = 0;
    const sections = this.message.panelRefSections ?? [];
    for (let i = 0; i < sectionIndex; i++) {
      priorPanelRefs += sections[i].refs.length;
    }
    return SOURCE_ROW_STAGGER_MS * (sourceCount + priorPanelRefs + refIndex + 1);
  }

  get isBotPending(): boolean {
    return this.message.type === 'bot' && !this.message.content?.trim();
  }

  get showInlineStreamTyping(): boolean {
    return (
      this.message.type === 'bot' &&
      !!this.message.replyStreaming &&
      !!this.message.content?.trim()
    );
  }

  sourceLinkHref(source: ChatMessageSource): string {
    if (this.canNavigateToProjectItem(source)) {
      return '#';
    }
    return source.url || 'javascript:void(0)';
  }

  sourceLinkTarget(source: ChatMessageSource): string | null {
    if (this.canNavigateToProjectItem(source)) return null;
    const u = source.url?.trim();
    return u && /^https?:\/\//i.test(u) ? '_blank' : null;
  }

  sourceLinkRel(source: ChatMessageSource): string | null {
    return this.sourceLinkTarget(source) === '_blank' ? 'noopener noreferrer' : null;
  }

  onSourceLinkClick(event: MouseEvent, source: ChatMessageSource): void {
    if (source.attachmentId) {
      event.preventDefault();
      this.openCitationAttachmentPdf(source);
      return;
    }
    if (this.canNavigateToProjectItem(source)) {
      event.preventDefault();
      this.lumiService.requestOpenProjectItem({
        projectId: this.sourceNavigationProjectId!,
        projectSlug: this.sourceNavigationProjectSlug!,
        itemId: source.itemId!,
        itemContentId: source.itemContentId,
      });
      return;
    }
    if (!source.url || source.url === 'javascript:void(0)') {
      event.preventDefault();
    }
  }

  onPanelRefClick(event: MouseEvent, ref: ChatMessagePanelRef): void {
    if (!this.canNavigateToPanelRef()) {
      event.preventDefault();
      return;
    }
    event.preventDefault();
    this.lumiService.requestOpenPanelAuxiliary({
      projectId: this.sourceNavigationProjectId!,
      tabId: ref.tabId,
      entityId: ref.entityId,
      searchQuery: ref.searchQuery,
    });
  }

  private canNavigateToPanelRef(): boolean {
    return this.sourceNavigationProjectId != null;
  }

  private canNavigateToProjectItem(source: ChatMessageSource): boolean {
    return (
      source.itemId != null &&
      this.sourceNavigationProjectId != null &&
      !!this.sourceNavigationProjectSlug
    );
  }

  private openCitationAttachmentPdf(source: ChatMessageSource): void {
    const rawId = source.attachmentId?.trim();
    if (!rawId) return;
    const attachmentId = parseInt(rawId, 10);
    if (Number.isNaN(attachmentId)) return;
    this.apiService
      .show(Attachment, attachmentId)
      .pipe(take(1))
      .subscribe((attachment) => {
        const a = attachment as Attachment;
        if (!a.file) return;
        this.modalService
          .open(
            Annotation2Component,
            {
              attachment: new Attachment(a),
              editable: false,
              disableAnnotations: true,
              initialPage: this.citationPageForViewer(source),
            },
            { customClass: 'pdfViewer' },
          )
          .pipe(take(1))
          .subscribe();
      });
  }

  private citationPageForViewer(source: ChatMessageSource): number | undefined {
    const p = source.citationPage;
    if (p != null && !Number.isNaN(Number(p))) {
      const n = Math.floor(Number(p));
      if (n >= 1) return n;
      if (n === 0) return 1;
    }
    return undefined;
  }

  giveFeedback(type: 'up' | 'down'): void {
    if (!this.message.messageId) return;
    this.feedbackGiven = null;
    requestAnimationFrame(() => {
      this.feedbackGiven = type;
      if (type === 'up') {
        this.upvote.emit();
      } else {
        this.downvote.emit();
      }
    });
  }
}
